import {
  PROTOCOL_VERSION,
  SPECTATOR_SEAT,
  decodeServerMessage,
  type ClientMessage,
  type GameDefinition,
  type FighterInput,
  type Lobby,
  type Move,
  type Replay,
  type ReplaySummary,
  type RoomFights,
  type RoomSeat,
  type CardRole,
  type SupportCardId,
  type ServerMessage,
} from '@archon3d/core';
import { contentDigest } from '@archon3d/content';
import { sessionToken } from './accountSession';

/** What a host asks for: the board, the seat they take and the plan for every side. */
export interface CreateRoomOptions {
  definition?: GameDefinition;
  boardId: string;
  /** How many sides play (open and AI seats together). */
  players: number;
  /** The host's own seat, a faction index. */
  seat: number;
  name: string;
  /** The chess clock: seconds per move, 0 for none. */
  clockSeconds: number;
  /** One plan per side of the board; without it the first `players` seats are open. */
  seats?: RoomSeat[];
  fights?: RoomFights;
  humanFriendlyAi?: boolean;
  engagement?: 'immediate' | 'muster';
}

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'closed';

/** What the room client tells its listeners: every server message, plus its own connection. */
export type RoomEvent =
  ServerMessage | { t: 'connection'; state: ConnectionState; attempt?: number; detail?: string };

export interface Membership {
  code: string;
  /** Faction index, or `SPECTATOR_SEAT` when only watching. */
  seat: number;
  token: string;
}

export interface JoinedRoom {
  code: string;
  seat: number;
  lobby: Lobby;
}

/** The little of a WebSocket the client uses, so tests can hand in a fake. */
export interface WebSocketLike {
  send(data: string): void;
  close(): void;
  onopen: (() => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event?: { code?: number; reason?: string }) => void) | null;
  onerror: (() => void) | null;
}

export interface RoomClientDeps {
  connect?: (url: string) => WebSocketLike;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  schedule?: (fn: () => void, ms: number) => () => void;
  /** Milliseconds on a steady clock; `performance.now` by default. */
  now?: () => number;
}

export class RoomClientError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'RoomClientError';
  }
}

const STORAGE_KEY = 'archon3d.room';
const REQUEST_TIMEOUT_MS = 15_000;
/**
 * Waits between reconnection attempts; the last one repeats for as long as it takes. The AI
 * stands in for a dropped seat (D-29), and it is the person's again whenever they are back.
 */
const RECONNECT_DELAYS_MS = [1000, 2000, 3000, 5000, 8000, 10_000];
/**
 * How long a player whose connection dropped is counted down before their screen says the AI
 * has taken their seat (D-29). A resume after that asks the AI to keep the seat until they are
 * ready to take it back, so nobody is put back into the game before they have said OK.
 */
export const STAND_IN_NOTICE_MS = 3000;
/**
 * A quiet connection is pinged this often. A socket can die without a word (Wi-Fi gone, a laptop
 * lid closed), and nothing else would notice until the player next moved.
 */
const KEEPALIVE_MS = 3000;
/** A ping unanswered for this long means the connection is gone. */
const ANSWER_WAIT_MS = 7000;
/** Server answers that mean the membership is gone for good. */
const FATAL_CODES = new Set(['bad-token', 'expired', 'no-room']);
/** Whether a failure means the seat is gone for good, rather than the way to it for now. */
export function isFatal(err: unknown): err is RoomClientError {
  return err instanceof RoomClientError && FATAL_CODES.has(err.code);
}
/** What a page built for another protocol or other content is told. */
const UPDATE_REQUIRED = 'Game update required. Refresh the page to carry on.';
/** The server's close when the same seat was taken up on another connection (`wsServer.ts`). */
const RESUMED_ELSEWHERE = 'resumed elsewhere';

/**
 * The client's end of the room protocol: one WebSocket, typed requests for creating, joining
 * and resuming, and a stream of events for the session and the menu. A dropped connection is
 * retried with the seat's token for as long as the server keeps the seat. The token also lives in
 * the browser's local storage, so a reloaded page, a new tab and a browser the headset closed all
 * find their way back into the game (x-multiplayer#4). Only one window plays a seat: the one that
 * last took it up.
 */
export class RoomClient {
  membership: Membership | null = null;
  private socket: WebSocketLike | null = null;
  private connection: ConnectionState = 'idle';
  private readonly listeners = new Set<(event: RoomEvent) => void>();
  private readonly waiting = new Map<
    ServerMessage['t'],
    { resolve: (message: ServerMessage) => void; reject: (err: Error) => void; cancel: () => void }
  >();
  private hello: { resolve: () => void; reject: (err: Error) => void } | null = null;
  /** The connection being opened by `connect`, which every caller meanwhile waits for. */
  private opening: Promise<void> | null = null;
  private closedByUs = false;
  /** The server closed this connection because the seat was taken up on another. */
  private elsewhere = false;
  private reconnectAttempt = 0;
  private reconnectTimer: (() => void) | null = null;
  /** When the connection dropped, until a resume succeeds; how long the player has been told. */
  private droppedAt: number | null = null;
  private keepaliveTimer: (() => void) | null = null;
  /** When the unanswered ping went out; any message from the server answers it. */
  private pingedAt: number | null = null;
  private lastBeat: number | null = null;
  private readonly connectSocket: (url: string) => WebSocketLike;
  private readonly storage: RoomClientDeps['storage'];
  private readonly schedule: (fn: () => void, ms: number) => () => void;
  private readonly now: () => number;

  constructor(
    readonly url: string,
    deps: RoomClientDeps = {},
  ) {
    // The browser's WebSocket has richer handler types; structurally it does what is needed.
    this.connectSocket =
      deps.connect ??
      ((target) =>
        new WebSocket(
          target,
          sessionToken(target) ? ['rpg-arena', `auth.${sessionToken(target)}`] : undefined,
        ) as unknown as WebSocketLike);
    this.storage = deps.storage === undefined ? localStorageOrNull() : deps.storage;
    this.schedule =
      deps.schedule ??
      ((fn, ms) => {
        const handle = setTimeout(fn, ms);
        return () => clearTimeout(handle);
      });
    this.now = deps.now ?? (() => performance.now());
  }

  get state(): ConnectionState {
    return this.connection;
  }

  /** The local player's seat (faction index), or −1 outside a room and when only watching. */
  get seat(): number {
    return this.membership?.seat ?? SPECTATOR_SEAT;
  }

  /** A membership saved by an earlier page load, if any. */
  static stored(storage: RoomClientDeps['storage'] = localStorageOrNull()): Membership | null {
    try {
      const raw = storage?.getItem(STORAGE_KEY);
      const value = raw ? (JSON.parse(raw) as Partial<Membership>) : null;
      if (
        value &&
        typeof value.code === 'string' &&
        typeof value.seat === 'number' &&
        typeof value.token === 'string'
      )
        return { code: value.code, seat: value.seat, token: value.token };
    } catch {
      // Unreadable storage: nothing to resume.
    }
    return null;
  }

  subscribe(listener: (event: RoomEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Opens the connection and waits for the server's greeting. A second call while the first is
   * still on its way waits for the same greeting, where it used to open another socket and leave
   * the first call waiting for ever (net-server:bugs#7).
   */
  connect(): Promise<void> {
    if (this.connection === 'connected') return Promise.resolve();
    if (this.opening) return this.opening;
    this.closedByUs = false;
    const opening = this.open('connecting').finally(() => {
      if (this.opening === opening) this.opening = null;
    });
    this.opening = opening;
    return opening;
  }

  async create(setup: CreateRoomOptions): Promise<JoinedRoom> {
    await this.connect();
    const reply = await this.request({ t: 'create', ...setup }, 'created');
    return this.remember(reply);
  }

  /** Joins a room; `watch` asks for no seat, otherwise one is taken when free. */
  async join(code: string, name: string, watch = false): Promise<JoinedRoom> {
    await this.connect();
    const reply = await this.request(
      watch ? { t: 'join', code, name, watch } : { t: 'join', code, name },
      'joined',
    );
    return this.remember(reply);
  }

  /** The finished games the server still remembers, newest first. */
  async listReplays(): Promise<ReplaySummary[]> {
    await this.connect();
    return (await this.request({ t: 'replays' }, 'replays')).replays;
  }

  async fetchReplay(id: string): Promise<Replay> {
    await this.connect();
    return (await this.request({ t: 'replay', id }, 'replay')).replay;
  }

  /** Takes a saved seat back, e.g. after a page reload. */
  async resume(
    membership: Membership | null = RoomClient.stored(this.storage),
  ): Promise<JoinedRoom> {
    if (!membership) throw new RoomClientError('no-room', 'Nothing to resume');
    this.membership = membership;
    let socket: WebSocketLike | null = null;
    try {
      await this.connect();
      socket = this.socket;
      const reply = await this.request({ t: 'resume', token: membership.token }, 'joined');
      return this.remember(reply);
    } catch (err) {
      // Only an answer that the seat is gone forgets it. A server still waking, a Wi-Fi blip or a
      // slow answer keeps it, and the connection's own retries take the seat up once they get
      // through (x-multiplayer#3).
      if (isFatal(err)) {
        this.forget();
        this.close();
      } else this.retry(socket);
      throw err;
    }
  }

  /** Leaves the room and drops the connection; nothing is resumed afterwards. */
  leave(): void {
    if (this.membership && this.connection === 'connected') this.send({ t: 'leave' });
    this.forget();
    this.close();
  }

  start(): void {
    this.send({ t: 'start' });
  }

  sendMove(move: Move): void {
    this.send({ t: 'move', move });
  }

  sendCardChoice(cardId: SupportCardId | null, role?: CardRole): void {
    this.send(role === undefined ? { t: 'cardChoice', cardId } : { t: 'cardChoice', cardId, role });
  }

  /** The person has read the revealed cards of their Card clash. */
  sendCardContinue(): void {
    this.send({ t: 'cardContinue' });
  }

  sendInput(tick: number, input: FighterInput): void {
    this.send({ t: 'input', tick, input });
  }

  sendForfeit(): void {
    this.send({ t: 'forfeit' });
  }

  /** Takes the seat back from the AI that stood in while this person was away. */
  reclaim(): void {
    this.send({ t: 'reclaim' });
  }

  /** Offers the other sides a draw (D-46). */
  sendDrawOffer(): void {
    this.send({ t: 'offerDraw' });
  }

  /** Answers the draw offered to this person's side. */
  sendDrawAnswer(accept: boolean): void {
    this.send({ t: 'answerDraw', accept });
  }

  /** Lets the connection go for good; `detail` says why, where the player sees it. */
  close(detail?: string): void {
    this.closedByUs = true;
    this.reconnectTimer?.();
    this.reconnectTimer = null;
    this.droppedAt = null;
    this.stopKeepalive();
    const socket = this.socket;
    this.socket = null;
    socket?.close();
    this.failWaiting(new RoomClientError('closed', 'Connection closed'));
    this.setConnection('closed', undefined, detail);
  }

  private send(message: ClientMessage): void {
    if (this.socket && this.connection === 'connected') this.socket.send(JSON.stringify(message));
  }

  private open(state: 'connecting' | 'reconnecting', attempt?: number): Promise<void> {
    this.socket?.close();
    this.setConnection(state, attempt);
    return new Promise<void>((resolve, reject) => {
      let socket: WebSocketLike;
      try {
        socket = this.connectSocket(this.url);
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      this.socket = socket;
      this.hello = { resolve, reject };
      const timeout = this.schedule(() => {
        if (this.hello?.reject === reject) {
          this.hello = null;
          socket.close();
          reject(new RoomClientError('timeout', 'The server did not answer'));
        }
      }, REQUEST_TIMEOUT_MS);
      socket.onopen = () => undefined;
      socket.onmessage = (event) => {
        if (this.socket !== socket) return;
        // Any word from the server shows the connection is alive.
        this.pingedAt = null;
        const message = decodeServerMessage(String(event.data));
        if (message) this.receive(message);
      };
      socket.onerror = () => undefined;
      socket.onclose = (event) => {
        if (this.socket === socket && event?.reason === RESUMED_ELSEWHERE) this.elsewhere = true;
        timeout();
        if (this.hello?.reject === reject) {
          this.hello = null;
          reject(new RoomClientError('unreachable', 'Could not reach the server'));
        }
        this.lost(socket);
      };
    });
  }

  /** This socket is gone, closed or silent: stop listening to it and try to get the seat back. */
  private lost(socket: WebSocketLike): void {
    if (this.socket !== socket) return;
    this.socket = null;
    this.stopKeepalive();
    this.failWaiting(new RoomClientError('closed', 'Connection lost'));
    this.dropped();
  }

  private startKeepalive(): void {
    this.stopKeepalive();
    this.lastBeat = this.now();
    this.keepaliveTimer = this.schedule(() => this.beat(), KEEPALIVE_MS);
  }

  private stopKeepalive(): void {
    this.keepaliveTimer?.();
    this.keepaliveTimer = null;
    this.pingedAt = null;
    this.lastBeat = null;
  }

  /** Pings a quiet connection, and gives it up when the last ping has gone unanswered. */
  private beat(): void {
    this.keepaliveTimer = null;
    const socket = this.socket;
    if (!socket || this.connection !== 'connected') return;
    const now = this.now();
    // A beat far behind time means this page was stalled (a heavy load, a hidden tab): answers
    // may be waiting behind it, so it does not judge the connection this time.
    const stalled = this.lastBeat !== null && now - this.lastBeat > 2 * KEEPALIVE_MS;
    this.lastBeat = now;
    if (!stalled && this.pingedAt !== null && now - this.pingedAt >= ANSWER_WAIT_MS) {
      socket.close();
      this.lost(socket);
      return;
    }
    if (this.pingedAt === null) {
      this.pingedAt = now;
      socket.send(JSON.stringify({ t: 'ping', sent: now } satisfies ClientMessage));
    }
    this.keepaliveTimer = this.schedule(() => this.beat(), KEEPALIVE_MS);
  }

  private receive(message: ServerMessage): void {
    if (message.t === 'hello') {
      const greeting = this.hello;
      this.hello = null;
      // Another protocol, or other content (boards, characters, arenas), plays another game
      // (core-net-balance:bugs#7). The page says so, in the menu and on the table alike
      // (x-multiplayer#13), and keeps the seat's token for the updated page to take the seat up.
      if (
        message.protocol !== PROTOCOL_VERSION ||
        (message.content !== undefined && message.content !== contentDigest())
      ) {
        greeting?.reject(new RoomClientError('unsupported', UPDATE_REQUIRED));
        this.close(UPDATE_REQUIRED);
        return;
      }
      this.reconnectAttempt = 0;
      this.setConnection('connected');
      this.startKeepalive();
      greeting?.resolve();
      return;
    }
    if (message.t === 'error') {
      const waiting = [...this.waiting.values()];
      this.waiting.clear();
      for (const w of waiting) {
        w.cancel();
        w.reject(new RoomClientError(message.code, message.message));
      }
      if (waiting.length === 0) this.emit(message);
      return;
    }
    // The seat went to the AI for good (a run of clock timeouts): from now on this person
    // watches. An AI standing in keeps the seat for them, so nothing changes here.
    if (
      message.t === 'seatChanged' &&
      message.kind === 'ai' &&
      !message.standIn &&
      this.membership &&
      message.seat === this.membership.seat
    ) {
      this.membership = { ...this.membership, seat: SPECTATOR_SEAT };
      this.store();
    }
    const waiting = this.waiting.get(message.t);
    if (waiting) {
      this.waiting.delete(message.t);
      waiting.cancel();
      waiting.resolve(message);
    }
    this.emit(message);
  }

  private request<T extends ServerMessage['t']>(
    message: ClientMessage,
    reply: T,
  ): Promise<Extract<ServerMessage, { t: T }>> {
    return new Promise((resolve, reject) => {
      if (!this.socket || this.connection !== 'connected') {
        reject(new RoomClientError('closed', 'Not connected'));
        return;
      }
      const cancel = this.schedule(() => {
        this.waiting.delete(reply);
        reject(new RoomClientError('timeout', 'The server did not answer'));
      }, REQUEST_TIMEOUT_MS);
      this.waiting.set(reply, {
        resolve: (m) => resolve(m as Extract<ServerMessage, { t: T }>),
        reject,
        cancel,
      });
      this.socket.send(JSON.stringify(message));
    });
  }

  private remember(reply: { code: string; seat: number; token: string; lobby: Lobby }): JoinedRoom {
    this.membership = { code: reply.code, seat: reply.seat, token: reply.token };
    this.store();
    return { code: reply.code, seat: reply.seat, lobby: reply.lobby };
  }

  private store(): void {
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.membership));
    } catch {
      // No storage: a reload will not find its way back, the game still plays.
    }
  }

  private forget(): void {
    this.membership = null;
    try {
      this.storage?.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to clear.
    }
  }

  /** The socket closed on its own: keep trying to get the seat back until the server says no. */
  private dropped(): void {
    if (this.closedByUs) return;
    if (!this.membership) {
      this.setConnection('closed');
      return;
    }
    // Another window (or device) took the seat up: this one stops, or the two would take it from
    // each other for ever. The seat's token stays for that window.
    if (this.elsewhere) {
      this.elsewhere = false;
      this.membership = null;
      this.close();
      this.setConnection('closed', undefined, 'This game went on in another window.');
      return;
    }
    this.droppedAt ??= this.now();
    const delay =
      RECONNECT_DELAYS_MS[Math.min(this.reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)]!;
    this.reconnectAttempt += 1;
    this.setConnection('reconnecting', this.reconnectAttempt);
    this.reconnectTimer = this.schedule(() => {
      this.reconnectTimer = null;
      void this.reconnect();
    }, delay);
  }

  private async reconnect(): Promise<void> {
    const membership = this.membership;
    if (!membership || this.closedByUs) return;
    let socket: WebSocketLike | null = null;
    try {
      await this.open('reconnecting', this.reconnectAttempt);
      socket = this.socket;
      // Told by now that the AI took their seat: it keeps the seat until they say OK.
      const told =
        membership.seat !== SPECTATOR_SEAT &&
        this.droppedAt !== null &&
        this.now() - this.droppedAt >= STAND_IN_NOTICE_MS;
      await this.request(
        told
          ? { t: 'resume', token: membership.token, standIn: true }
          : { t: 'resume', token: membership.token },
        'joined',
      );
      this.droppedAt = null;
    } catch (err) {
      if (isFatal(err)) {
        this.forget();
        this.close();
        this.setConnection('closed', undefined, err.message);
        return;
      }
      // A connection that closed has scheduled the next attempt already. One still open but that
      // failed the resume (an error, no answer in time) would otherwise sit connected and out of
      // the room for good (net-server:quality#7): it is let go and tried again.
      this.retry(socket);
    }
  }

  /**
   * Lets the connection a resume failed on go, and tries again after a wait: only if it is still
   * the current one, since a newer one may already be on its way.
   */
  private retry(socket: WebSocketLike | null): void {
    if (this.closedByUs || !this.membership || !socket || this.socket !== socket) return;
    socket.close();
    this.lost(socket);
  }

  private failWaiting(err: Error): void {
    const waiting = [...this.waiting.values()];
    this.waiting.clear();
    for (const w of waiting) {
      w.cancel();
      w.reject(err);
    }
  }

  private setConnection(state: ConnectionState, attempt?: number, detail?: string): void {
    this.connection = state;
    this.emit({ t: 'connection', state, attempt, detail });
  }

  private emit(event: RoomEvent): void {
    for (const listener of [...this.listeners]) listener(event);
  }
}

function localStorageOrNull(): RoomClientDeps['storage'] {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
